import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    Typography,
    CircularProgress,
    Alert,
    Box,
    Divider,
    IconButton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmDialog from './ConfirmDialog'; // Reuse confirm dialog
import { useListMessageComments, useDeleteMessageComment } from '../services/appService';

// Helper to format date/time
const formatDate = (dateInput) => {
    if (!dateInput) return 'N/A';
    try {
        const date = new Date(dateInput);
        return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleString();
    } catch (e) {
        return 'Invalid Date';
    }
};

function MessageCommentsModal({ open, onClose, appId, groupId, message }) {
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const listCommentsHook = useListMessageComments();
    const deleteCommentHook = useDeleteMessageComment();

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [commentToDelete, setCommentToDelete] = useState(null);

    const fetchComments = useCallback(async () => {
        if (!appId || !groupId || !message?.id) {
            setComments([]);
            return;
        }
        setLoading(true);
        setError('');
        try {
            const data = await listCommentsHook(appId, groupId, message.id);
            setComments(data || []);
        } catch (err) {
            console.error("Error fetching comments:", err);
            setError(err.message || 'Failed to load comments.');
        } finally {
            setLoading(false);
        }
    }, [appId, groupId, message?.id, listCommentsHook]);

    useEffect(() => {
        if (open) {
            fetchComments();
        } else {
            setComments([]);
            setError('');
        }
    }, [open, fetchComments]);

    const handleOpenConfirmDialog = (commentId) => {
        setCommentToDelete(commentId);
        setConfirmDialogOpen(true);
    };

    const handleCloseConfirmDialog = () => {
        setCommentToDelete(null);
        setConfirmDialogOpen(false);
    };

    const handleDeleteConfirm = async () => {
        if (!commentToDelete || !appId || !groupId || !message?.id) return;
        // Note: setLoading could be managed by a hook if we had one for delete
        try {
            await deleteCommentHook(appId, groupId, message.id, commentToDelete);
            fetchComments(); // Refresh list
        } catch (err) {
            console.error("Error deleting comment:", err);
            setError(err.message || 'Failed to delete comment.');
        } finally {
            handleCloseConfirmDialog();
        }
    };

    const messagePreview = message?.text ? 
        (message.text.length > 100 ? message.text.substring(0, 100) + '...' : message.text) 
        : (message?.image_url ? <i>Image Message</i> : 'Selected Message');

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
                <DialogTitle>Comments on "{messagePreview}"</DialogTitle>
                <DialogContent dividers>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '150px' }}>
                            <CircularProgress />
                        </Box>
                    ) : comments.length === 0 ? (
                        <Typography sx={{ textAlign: 'center', mt: 2 }}>No comments found for this message.</Typography>
                    ) : (
                        <List disablePadding>
                            {comments.map((comment, index) => (
                                <React.Fragment key={comment.id}>
                                    <ListItem 
                                        alignItems="flex-start"
                                        secondaryAction={
                                            <IconButton 
                                                edge="end" 
                                                aria-label="delete comment" 
                                                onClick={() => handleOpenConfirmDialog(comment.id)}
                                                size="small"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText
                                            primary={comment.text}
                                            secondary={
                                                <>
                                                    <Typography
                                                        sx={{ display: 'inline' }}
                                                        component="span"
                                                        variant="body2"
                                                        color="text.primary"
                                                    >
                                                        {comment.sender || 'Unknown Sender'} 
                                                    </Typography>
                                                    {` — ${formatDate(comment.created_at)}`}
                                                </>
                                            }
                                        />
                                    </ListItem>
                                    {index < comments.length - 1 && <Divider variant="inset" component="li" />}
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={confirmDialogOpen}
                onClose={handleCloseConfirmDialog}
                onConfirm={handleDeleteConfirm}
                title="Delete Comment?"
                message={`Are you sure you want to delete comment ID: ${commentToDelete}? This action cannot be undone.`}
            />
        </>
    );
}

export default MessageCommentsModal; 